import { Button, Group, LoadingOverlay, ScrollArea, Stack, Text, useMantineTheme } from '@mantine/core';

import { useNavigate, useParams } from 'react-router-dom';

import { useForm } from '@mantine/form';

import { type FileWithPath } from '@mantine/dropzone';

import { useCallback, useMemo, useState } from 'react';

import useEnsisMutation from '../../hooks/useEnsisMutation';
import { INVALID_FILE_ERROR_MESSAGE } from '../../utils/apiUtils';
import { DEFAULT_FAILURE_MESSAGE, showFailureNotification } from '../../utils/mantineUtils';
import { logAnalyticsEvent } from '../../utils/analyticsUtils';

import WizardContent from './WizardContent';
import { CompanyDetailsFormFields, InitializeProposalForm, SelectResponseFormat } from '../../components';
import { type InitializeProposalFormValues } from '../../components/Forms/InitializeProposalForm';
import SelectProposalSpacing from '../../components/SelectProposalSpacing';
import {
  type CompanyDetailsFormValues, defaultCompanyDetailsFormInitialValues, getInitialValuesFromKeyValuesObj
} from '../../components/Forms/CompanyDetailsFormFields/CompanyDetailsFormFields';
import { DefaultKeys, keyValueListToObj, validatePhoneNumber } from '../../utils/keyValueUtils';
import { addressToString } from '../../utils/addressUtils';
import useEnsisQuery from '../../hooks/useEnsisQuery';

type CreateProposalFormValues = {
  outlineOutputFormat?: string
  spacing?: number
} & InitializeProposalFormValues & CompanyDetailsFormValues;

interface Props {
  onDone: () => void
}

const FORMATTING_HELPER_TEXT = 'Set the default line spacing (e.g., single, 1.5, double) and choose whether outline ' +
  'responses are written in paragraph or bullet-point format. These settings apply to this proposal only.';
const SUBTITLE_COPY = 'Start by naming your proposal and importing the opportunity.' +
  'Then, customize win themes, formatting, and other details if you\'d like.';
const PROPOSAL_DETAILS_HELPER_TEXT = 'These optional fields are pre-filled with your organization\'s default ' +
  'settings. You can update them for this proposal only. ' +
  'To change the defaults for all future proposals, visit your Settings page.';
const CreateProposal: React.FC<Props> = (props: Props) => {
  const { onDone } = props;
  const [skipFileUpload, setSkipFileUpload] = useState(false);
  const { organization } = useParams();
  const navigate = useNavigate();

  const { other: colorVars } = useMantineTheme();
  const validateFiles = useCallback((files: FileWithPath[]) => {
    if (skipFileUpload) {
      return null;
    }
    if (files === null || files.length === 0) {
      return 'Please select file(s) to upload';
    }
    return null;
  }, [skipFileUpload]);
  const orgKeyValueQueryRoute = `/app/organizations/${organization}/key-value-items` as const;
  const { data: orgKeyValues } = useEnsisQuery(
    orgKeyValueQueryRoute
  );

  const placeholderValues = useMemo(
    () => getInitialValuesFromKeyValuesObj(keyValueListToObj(orgKeyValues?.items ?? [])),
    [orgKeyValues?.items]
  );

  const initialValues = useMemo(() => (
    {
      files: [] as FileWithPath[],
      proposalName: '',
      additionalContext: '',
      outlineOutputFormat: 'BULLET_POINTS',
      spacing: 1,
      ...defaultCompanyDetailsFormInitialValues
    }), []);

  const form = useForm<CreateProposalFormValues>({
    initialValues,
    validate: {
      files: validateFiles,
      proposalName: (value: string) => (value.trim().length < 1 ? 'Proposal name is required' : null),
      phone: validatePhoneNumber
    }
  });

  const submitProposalMutation = useEnsisMutation(
    '/app/proposals',
    {
      contentType: 'multipart/form-data',
      requestType: 'post',
      showSuccessMessage: false,
      showFailureMessage: false,
      queryKeysToInvalidate: ['/app/proposals'],
      onSuccess: (data) => {
        navigate(`/org/${organization}/proposals/create-proposal/${data.uid}`);
        onDone();
      },
      onError: (error) => {
        let errorMessage = DEFAULT_FAILURE_MESSAGE;
        if (error?.response?.status === 422) {
          errorMessage = INVALID_FILE_ERROR_MESSAGE;
        }
        showFailureNotification(errorMessage);
      }
    }
  );

  const onSubmitForm = useCallback((values: CreateProposalFormValues) => {
    logAnalyticsEvent('CreateProposal');
    const {
      proposalName,
      files,
      additionalContext,
      outlineOutputFormat,
      spacing,
      phone, email, ein, authorizedRep, name, ...addressValues
    } = values;
    const items = [
      {
        key: DefaultKeys.PHONE,
        value: phone
      },
      {
        key: DefaultKeys.ADDRESS,
        value: addressToString(addressValues)
      },
      {
        key: DefaultKeys.COMPANY_NAME,
        value: name
      },
      {
        key: DefaultKeys.EIN,
        value: ein
      },
      {
        key: DefaultKeys.COMPANY_EMAIL,
        value: email
      },
      {
        key: DefaultKeys.AUTHORIZED_REP,
        value: authorizedRep
      }
    ];

    submitProposalMutation.mutate({
      name: proposalName,
      files: files as unknown as string[],
      additional_context: additionalContext,
      organization_slug: organization ?? '',
      outline_output_format: outlineOutputFormat,
      spacing,
      key_value_items: JSON.stringify(items),
      generate_fast_draft: false
    });
  }, [submitProposalMutation.mutate]);

  const proposalDetailsSection = (
    <Stack gap={0}>
      <Text fz={18} fw={500}>
        {'Proposal Details (Optional)'}
      </Text>
      <Text pb={12} fz='sm' c={colorVars.secondaryTextColor}>
        {PROPOSAL_DETAILS_HELPER_TEXT}
      </Text>
      <CompanyDetailsFormFields
        form={form}
        initialValues={initialValues}
        placeholderValues={placeholderValues as CreateProposalFormValues}
        canEdit
      />
    </Stack>
  );

  const proposalFormattingSection = !skipFileUpload && (
    <Stack gap={0}>
      <Text fz={18} fw={500}>
        {'Formatting (Optional)'}
      </Text>
      <Text pb={12} fz='sm' c={colorVars.secondaryTextColor}>
        {FORMATTING_HELPER_TEXT}
      </Text>
      <Group>
        <SelectResponseFormat
          onSubmit={(format: string) => { form.setFieldValue('outlineOutputFormat', format); }}
        />
        <SelectProposalSpacing
          initialSpacing={1}
          onSubmit={(spacing: number) => { form.setFieldValue('spacing', spacing); }}
        />
      </Group>
    </Stack>
  );
  return (
    <WizardContent
      flex={1}
      titleCopy="Let's get started"
      subtitleCopy={SUBTITLE_COPY}
    >
      <LoadingOverlay visible={submitProposalMutation.isPending} />
      <ScrollArea.Autosize mah={'100%'}>
        <form onSubmit={form.onSubmit(onSubmitForm)}>
          <Stack mx={10}>
            <InitializeProposalForm
              form={form}
              skipFileUpload={skipFileUpload}
              setSkipFileUpload={setSkipFileUpload}
              winThemesTitle={<Text fz={18} fw={500}> {'Win Themes / Differentiators (Optional)'} </Text>}
              textProps={{ fw: 400, c: colorVars.secondaryTextColor }}
            />
            {proposalDetailsSection}
            {proposalFormattingSection}
            <Group justify='end' p={0} gap={0} m={0}>
              <Button type='submit' disabled={!form.isValid()}>
                Next
              </Button>
            </Group>
          </Stack>
        </form>
      </ScrollArea.Autosize>
    </WizardContent >
  );
};

export default CreateProposal;
